// pages/demirbas/dialogs/create/DemirbasDraft.js
import {
	calculateFaturaTotals,
	normalizeFaturaCurrency,
	parseFaturaQuantity,
	roundFaturaAmount,
} from "../faturaCurrency.js";
import { readAmortismanSelection } from "../../../amortisman/amortismanUtils.js";

export class DemirbasDraft {
	constructor(createDialog) {
		// this.demirbas_id = crypto.randomUUID(); // frontta üret
		this.createMeta = this.#readCreateMode(createDialog);
		this.temel = this.#readTemel(createDialog);
		this.zimmetLokasyon = this.#readZimmetLokasyon(createDialog);
		this.faturalar = this.#readFaturalar(createDialog);
	}

	#v(root, sel) {
		return root.querySelector(sel)?.value ?? "";
	}

	#trim(root, sel) {
		return this.#v(root, sel).trim();
	}

	#faturaField(card, fieldName) {
		return card.querySelector(`[data-fatura-field="${fieldName}"]`);
	}

	#emptyToNull(val) {
		if (val === undefined || val === null) return null;
		const s = String(val).trim();
		return s === "" ? null : s;
	}

	#numFromText(text) {
		// "12,5" -> 12.5 | "12.5" -> 12.5 | boş -> null
		if (text === undefined || text === null) return null;
		const s = String(text).trim().replace(",", ".");
		if (!s) return null;
		const n = Number(s);
		return Number.isFinite(n) ? n : null;
	}

	#numFromAutoNumericInput(inputEl) {
		// AutoNumeric instance'i input._an içinde
		const raw = inputEl?._an?.getNumericString?.();
		if (raw === null || raw === undefined || raw === "") return null;
		const n = Number(raw);
		return Number.isFinite(n) ? n : null;
	}

	#readCreateMode(dlg) {
		const repeatMode = Boolean(dlg.querySelector("#create_serial_mode_repeat")?.checked);
		const rawCount = String(this.#v(dlg, "#create_seri_tekrar_adedi") || "").replace(/\D+/g, "");
		const parsedCount = Number.parseInt(rawCount, 10);
		return {
			create_mode: repeatMode ? "bulk" : "single",
			create_count: repeatMode ? parsedCount : 1,
		};
	}

	#readTemel(dlg) {
		return {
			departman_id: this.#emptyToNull(this.#v(dlg, "#create_departman")),
			departman_urun_grubu_id: this.#emptyToNull(this.#v(dlg, "#create_urun_grubu")),
			urun_grubu_urun_alt_grubu_id: this.#emptyToNull(this.#v(dlg, "#create_urun_alt_grubu")),
			urun_alt_grubu_urun_cinsi_id: this.#emptyToNull(this.#v(dlg, "#create_urun_cinsi")),
			urun_cinsi_marka_id: this.#emptyToNull(this.#v(dlg, "#create_marka")),
			marka_model_id: this.#emptyToNull(this.#v(dlg, "#create_model")),

			seri_numarasi: this.#emptyToNull(this.#trim(dlg, "#create_seri_numarasi")),
			aciklama: this.#emptyToNull(this.#trim(dlg, "#create_aciklama")),
			barkod: null,
			urun_gorsel_path: dlg.querySelector("#create_urun_gorsel_path")?.value || null,
		};
	}
	#readZimmetLokasyon(dlg) {
		return {
			urun_durumu_id: this.#emptyToNull(this.#v(dlg, "#create_urun_durumu")),
			zimmet_durumu_id: this.#emptyToNull(this.#v(dlg, "#create_zimmet_durumu")),
			zimmetlenen_tc: this.#emptyToNull(this.#v(dlg, "#create_zimmetlenen_kisi")),
			zimmet_tarihi: this.#emptyToNull(this.#v(dlg, "#create_zimmet_tarihi")),

			lokasyon_id: this.#emptyToNull(this.#v(dlg, "#create_lokasyon")),
			oda_tanim_id: this.#emptyToNull(this.#v(dlg, "#create_oda")),
			yer_tanim_id: this.#emptyToNull(this.#v(dlg, "#create_yer")),
		};
	}

	#readFaturalar(dlg) {
		const list = dlg.querySelector("#faturaList");
		const cards = list ? [...list.querySelectorAll(".fatura-container")] : [];

		return cards.map((card) => {
			const fiyatInput = this.#faturaField(card, "birim_fiyat");
			const quantityInput = this.#faturaField(card, "birim_miktar");
			const kdvSelect = this.#faturaField(card, "kdv_oran");

			const birimFiyat = this.#numFromAutoNumericInput(fiyatInput); // raw numeric
			const rawBirimMiktar = String(quantityInput?.value ?? "").trim();
			const birimMiktar = rawBirimMiktar === "" ? 1 : parseFaturaQuantity(rawBirimMiktar);
			const kdvOrani = this.#numFromText(kdvSelect?.value); // 8 / 18
			const totals = calculateFaturaTotals({
				birimFiyat,
				birimMiktar,
				kdvOrani,
			});
			const amortismanSelection = readAmortismanSelection(
				this.#faturaField(card, "amortisman"),
			);

			return {
				fatura_numarasi: this.#emptyToNull(this.#faturaField(card, "fatura_no")?.value?.trim() ?? ""),
				satin_alma_tarihi: this.#emptyToNull(this.#faturaField(card, "satin_alma_tarih")?.value ?? ""),
				...amortismanSelection,

				birim_fiyat: roundFaturaAmount(birimFiyat),
				birim_miktar: birimMiktar,
				para_birimi: normalizeFaturaCurrency(this.#faturaField(card, "para_birimi")?.value),
				kdv_orani: roundFaturaAmount(kdvOrani),
				kdv_tutar: roundFaturaAmount(totals.kdvTutar),
				satin_alma_degeri: roundFaturaAmount(totals.satinAlmaDegeri),

				fatura_pdf_path: this.#emptyToNull(
					card.querySelector(".fatura-pdf-path")?.value || "",
				),
				fatura_aciklama: this.#emptyToNull(this.#faturaField(card, "fatura_aciklama")?.value?.trim() ?? ""),
			};
		});
	}

	toPayload() {
		return {
			// demirbas_id: this.demirbas_id,
			...this.createMeta,
			...this.temel,
			...this.zimmetLokasyon,
			faturalar: this.faturalar,
		};
	}
}
